import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getAllBills,
  getMyBills,
  getBillsByParty,
  getBillsWithPagination,
  getAllParties,
  getAllProcesses,
  createBill,
  updateBill,
  deleteBill,
  searchBills,
  clearErrors,
  clearMessage,
  resetSuccess,
} from "../../redux/billSlice";
import { getAllParties as fetchPartiesList, getMyParties as fetchMyPartiesList } from "../../redux/partySlice";
import SearchableSelect from "../common/SearchableSelect";
import { Helmet } from "react-helmet-async";

/** Party/process from API may be null, a string id, or a populated doc. */
const billRefId = (ref) => {
  if (ref == null || ref === "") return "";
  if (typeof ref === "string" || typeof ref === "number") return String(ref);
  if (typeof ref === "object" && ref._id != null) return String(ref._id);
  if (typeof ref === "object" && typeof ref.toString === "function") {
    const s = ref.toString();
    if (s && s !== "[object Object]") return s;
  }
  return "";
};

const Bill = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { bills, parties, processes, loading, error, success, message, pagination } =
    useSelector((state) => state.bill);
  const { user } = useSelector((state) => state.user);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    date: getTodayDate(),
    party: "",
    lotNumber: "",
    process: "",
    quantity: "",
    amount: "",
    total: "",
  });

  const [errors, setErrors] = useState({
    date: "",
    party: "",
    lotNumber: "",
    process: "",
    quantity: "",
    amount: "",
  });

  const [touched, setTouched] = useState({
    date: false,
    party: false,
    lotNumber: false,
    process: false,
    quantity: false,
    amount: false,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showMyBills, setShowMyBills] = useState(false);
  const [selectedPartyFilter, setSelectedPartyFilter] = useState("");

  const paginatedMode = !searchQuery.trim() && !selectedPartyFilter;
  const fetchPaginated = useCallback(
    (page = 1, limit = 20) => {
      dispatch(
        getBillsWithPagination({
          page,
          limit,
          my: showMyBills && user ? 1 : undefined,
        })
      );
    },
    [dispatch, showMyBills, user]
  );

  // Validation functions
  const validateDate = (date) => {
    if (!date) {
      return "Date is required";
    }
    return "";
  };

  const validateParty = (party) => {
    if (!party || !party.trim()) {
      return "Party is required";
    }
    return "";
  };

  const validateLotNumber = (lotNumber) => {
    if (!lotNumber || !lotNumber.trim()) {
      return "Lot number is required";
    }
    if (lotNumber.trim().length > 100) {
      return "Lot number cannot exceed 100 characters";
    }
    return "";
  };

  const validateProcess = (process) => {
    if (!process || !process.trim()) {
      return "Process is required";
    }
    return "";
  };

  const validateQuantity = (quantity) => {
    if (!quantity && quantity !== 0) {
      return "Quantity is required";
    }
    const quantityNum = parseFloat(quantity);
    if (isNaN(quantityNum) || quantityNum <= 0) {
      return "Quantity must be a valid positive number";
    }
    return "";
  };

  const validateAmount = (amount) => {
    if (!amount && amount !== 0) {
      return "Amount is required";
    }
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum)) {
      return "Amount must be a valid number";
    }
    if (amountNum < 0) {
      return "Amount cannot be negative";
    }
    return "";
  };

  const validateField = (field, value) => {
    let error = "";
    switch (field) {
      case "date":
        error = validateDate(value);
        break;
      case "party":
        error = validateParty(value);
        break;
      case "lotNumber":
        error = validateLotNumber(value);
        break;
      case "process":
        error = validateProcess(value);
        break;
      case "quantity":
        error = validateQuantity(value);
        break;
      case "amount":
        error = validateAmount(value);
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
    return error === "";
  };

  const validateForm = () => {
    const dateValid = validateField("date", formData.date);
    const partyValid = validateField("party", formData.party);
    const lotNumberValid = validateField("lotNumber", formData.lotNumber);
    const processValid = validateField("process", formData.process);
    const quantityValid = validateField("quantity", formData.quantity);
    const amountValid = validateField("amount", formData.amount);
    setTouched({
      date: true,
      party: true,
      lotNumber: true,
      process: true,
      quantity: true,
      amount: true,
    });
    return (
      dateValid &&
      partyValid &&
      lotNumberValid &&
      processValid &&
      quantityValid &&
      amountValid
    );
  };

  // Load bills with pagination (faster for 500+ bills), parties, and processes
  useEffect(() => {
    dispatch(getAllParties());
    dispatch(getAllProcesses());
    fetchPaginated(1, 20);
  }, [dispatch, fetchPaginated]);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        dispatch(clearErrors());
        dispatch(clearMessage());
        dispatch(resetSuccess());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error, dispatch]);

  // Auto-calculate total when amount or quantity changes
  useEffect(() => {
    if (formData.amount && formData.quantity) {
      const amountNum = parseFloat(formData.amount);
      const quantityNum = parseFloat(formData.quantity);
      if (
        !isNaN(amountNum) &&
        !isNaN(quantityNum) &&
        amountNum >= 0 &&
        quantityNum > 0
      ) {
        const total = (amountNum * quantityNum).toFixed(2);
        setFormData((prev) => ({ ...prev, total }));
      } else {
        setFormData((prev) => ({ ...prev, total: "" }));
      }
    } else {
      setFormData((prev) => ({ ...prev, total: "" }));
    }
  }, [formData.amount, formData.quantity]);

  // Auto-fill amount when process is selected
  useEffect(() => {
    if (formData.process) {
      const selectedProcess = processes.find((p) => p._id === formData.process);
      if (selectedProcess) {
        setFormData((prev) => ({
          ...prev,
          amount: selectedProcess.amount.toString(),
        }));
      }
    }
  }, [formData.process, processes]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "quantity" || name === "amount") {
      // Allow only numbers and decimal point
      const numericValue = value.replace(/[^0-9.]/g, "");
      // Prevent multiple decimal points
      const parts = numericValue.split(".");
      const finalValue =
        parts.length > 2
          ? parts[0] + "." + parts.slice(1).join("")
          : numericValue;

      setFormData((prev) => ({ ...prev, [name]: finalValue }));
      if (touched[name]) {
        validateField(name, finalValue);
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (touched[name]) {
        validateField(name, value);
      }
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const billData = {
      date: formData.date,
      party: formData.party,
      lotNumber: formData.lotNumber.trim(),
      process: formData.process,
      quantity: parseFloat(formData.quantity),
      amount: parseFloat(formData.amount),
    };

    try {
      if (isEditing) {
        await dispatch(updateBill({ id: editingId, billData })).unwrap();
      } else {
        await dispatch(createBill(billData)).unwrap();
      }

      resetForm();
      setShowModal(false);
      if (paginatedMode) {
        fetchPaginated(pagination?.page || 1, pagination?.limit || 20);
      } else if (searchQuery.trim()) {
        dispatch(searchBills(searchQuery));
      } else if (selectedPartyFilter) {
        dispatch(getBillsByParty(selectedPartyFilter));
      } else {
        fetchPaginated(1, 20);
      }
      // Refresh party list so Party page shows updated due/advance/totalPayment
      dispatch(fetchPartiesList());
      if (user) dispatch(fetchMyPartiesList());
    } catch (error) {
      // Error handled by Redux
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      date: getTodayDate(),
      party: "",
      lotNumber: "",
      process: "",
      quantity: "",
      amount: "",
      total: "",
    });
    setErrors({
      date: "",
      party: "",
      lotNumber: "",
      process: "",
      quantity: "",
      amount: "",
    });
    setTouched({
      date: false,
      party: false,
      lotNumber: false,
      process: false,
      quantity: false,
      amount: false,
    });
    setIsEditing(false);
    setEditingId(null);
  };

  // Handle edit
  const handleEdit = (bill) => {
    if (!bill?._id) return;

    const billDate = new Date(bill.date);
    const year = billDate.getFullYear();
    const month = String(billDate.getMonth() + 1).padStart(2, "0");
    const day = String(billDate.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;

    const partyId = billRefId(bill.party);
    const processId = billRefId(bill.process);

    setFormData({
      date: formattedDate,
      party: partyId,
      lotNumber: bill.lotNumber ?? "",
      process: processId,
      quantity: bill.quantity != null ? String(bill.quantity) : "",
      amount: bill.amount != null ? String(bill.amount) : "",
      total: bill.total != null ? String(bill.total) : "",
    });
    setIsEditing(true);
    setEditingId(bill._id);
    setShowModal(true);
    setErrors({
      date: "",
      party: "",
      lotNumber: "",
      process: "",
      quantity: "",
      amount: "",
    });
    setTouched({
      date: false,
      party: false,
      lotNumber: false,
      process: false,
      quantity: false,
      amount: false,
    });
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this bill?")) {
      try {
        await dispatch(deleteBill(id)).unwrap();
        if (paginatedMode) {
          fetchPaginated(pagination?.page || 1, pagination?.limit || 20);
        } else if (searchQuery.trim()) {
          dispatch(searchBills(searchQuery));
        } else if (selectedPartyFilter) {
          dispatch(getBillsByParty(selectedPartyFilter));
        } else {
          fetchPaginated(1, 20);
        }
        // Refresh party list so Party page shows updated due/advance/totalPayment
        dispatch(fetchPartiesList());
        if (user) dispatch(fetchMyPartiesList());
      } catch (error) {
        // Error handled by Redux
      }
    }
  };

  // Handle search
  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);
      if (query.trim()) {
        dispatch(searchBills(query));
      } else {
        fetchPaginated(1, 20);
      }
    },
    [dispatch, fetchPaginated]
  );

  // Handle party filter
  const handlePartyFilter = (partyId) => {
    setSelectedPartyFilter(partyId);
    if (partyId) {
      dispatch(getBillsByParty(partyId));
    } else {
      fetchPaginated(1, 20);
    }
  };

  // Toggle between all bills and my bills
  const handleToggleView = () => {
    setShowMyBills(!showMyBills);
    setSearchQuery("");
    setSelectedPartyFilter("");
  };

  const showToast = useCallback((message, type = "success") => {
    const toast = document.createElement("div");
    toast.className = `bill-toast bill-toast-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add("bill-toast-show"), 100);
    setTimeout(() => {
      toast.classList.remove("bill-toast-show");
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  }, []);

  useEffect(() => {
    if (success && message) {
      showToast(message, "success");
    }
    if (error) {
      showToast(error, "error");
    }
  }, [success, error, message, showToast]);

  // Filter bills based on search and party filter
  const filteredBills = bills.filter((bill) => {
    if (selectedPartyFilter && bill.party?._id !== selectedPartyFilter) {
      return false;
    }
    return true;
  });

  return (
    <>
      <Helmet>
        <title>Bill - RTS Books</title>
      </Helmet>
      <style>{`
        * {
          box-sizing: border-box;
        }

        @keyframes billFadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes billSlideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes billShake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }

        .bill-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
          background-size: 400% 400%;
          animation: billFadeIn 0.6s ease;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .bill-wrapper {
          max-width: 1600px;
          margin: 0 auto;
        }

        .bill-header {
          text-align: center;
          margin-bottom: 30px;
          color: white;
        }

        .bill-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 10px;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
        }

        .bill-card {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          margin-bottom: 20px;
        }

        .bill-search-bar {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .bill-search-input,
        .bill-filter-select {
          flex: 1;
          min-width: 200px;
          padding: 12px 16px;
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: white;
        }

        .bill-search-input:focus,
        .bill-filter-select:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }

        .bill-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .bill-btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
        }

        .bill-btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        }

        .bill-btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .bill-btn-secondary {
          background: #e1e5e9;
          color: #334155;
        }

        .bill-btn-secondary:hover {
          background: #cbd5e1;
        }

        .bill-btn-toggle {
          background: #10b981;
          color: white;
        }

        .bill-btn-toggle:hover {
          background: #059669;
        }

        .bill-btn-success {
          background: #10b981;
          color: white;
        }

        .bill-btn-success:hover {
          background: #059669;
          transform: translateY(-2px);
        }

        .bill-table-wrapper {
          overflow-x: auto;
          border-radius: 12px;
        }

        .bill-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 12px;
          overflow: hidden;
        }

        .bill-table thead {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .bill-table th {
          padding: 16px;
          text-align: left;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .bill-table td {
          padding: 16px;
          border-bottom: 1px solid #e1e5e9;
        }

        .bill-table tbody tr:hover {
          background: #f8f9fa;
        }

        .bill-table tbody tr:last-child td {
          border-bottom: none;
        }

        .bill-action-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-right: 8px;
        }

        .bill-btn-edit {
          background: #10b981;
          color: white;
        }

        .bill-btn-edit:hover {
          background: #059669;
          transform: translateY(-1px);
        }

        .bill-btn-delete {
          background: #ef4444;
          color: white;
        }

        .bill-btn-delete:hover {
          background: #dc2626;
          transform: translateY(-1px);
        }

        .bill-total {
          font-weight: 700;
          color: #059669;
          font-size: 1.1rem;
        }

        .bill-outstanding-due {
          color: #b91c1c;
          font-weight: 600;
        }

        .bill-status {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
        }
        .bill-status.paid { background: #dcfce7; color: #166534; }
        .bill-status.partial { background: #fef3c7; color: #b45309; }
        .bill-status.unpaid { background: #fee2e2; color: #b91c1c; }

        .bill-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          backdrop-filter: blur(4px);
        }

        .bill-modal-content {
          background: white;
          border-radius: 20px;
          padding: 30px;
          max-width: 700px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          animation: billSlideIn 0.3s ease;
        }

        .bill-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .bill-modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
        }

        .bill-modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #64748b;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .bill-modal-close:hover {
          background: #f1f5f9;
          color: #1e293b;
        }

        .bill-form-group {
          margin-bottom: 20px;
        }

        .bill-label {
          display: block;
          margin-bottom: 8px;
          color: #334155;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .bill-label.required::after {
          content: " *";
          color: #dc2626;
        }

        .bill-input,
        .bill-select {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: white;
        }

        .bill-input:focus,
        .bill-select:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }

        .bill-input.error,
        .bill-select.error {
          border-color: #dc2626;
          animation: billShake 0.5s ease;
        }

        .bill-error {
          color: #dc2626;
          font-size: 0.85rem;
          margin-top: 6px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .bill-total-display {
          background: #f0fdf4;
          border: 2px solid #86efac;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 20px;
          text-align: center;
        }

        .bill-total-label {
          font-size: 0.9rem;
          color: #166534;
          margin-bottom: 4px;
        }

        .bill-total-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #059669;
        }

        .bill-modal-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }

        .bill-loading {
          text-align: center;
          padding: 40px;
          color: #64748b;
        }

        .bill-empty {
          text-align: center;
          padding: 40px;
          color: #64748b;
        }

        .bill-toast {
          position: fixed;
          top: 20px;
          right: -300px;
          background: #fff;
          color: #333;
          padding: 15px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          z-index: 10000;
          transition: all 0.3s ease;
          max-width: 350px;
          font-weight: 500;
        }

        .bill-toast.bill-toast-success {
          border-left: 4px solid #059669;
        }

        .bill-toast.bill-toast-error {
          border-left: 4px solid #dc2626;
        }

        .bill-toast.bill-toast-show {
          right: 20px;
          animation: billSlideIn 0.3s ease;
        }

        /* Mobile Responsive - Phone First */
        @media (max-width: 768px) {
          .bill-container {
            padding: 15px;
          }

          .bill-title {
            font-size: 2rem;
          }

          .bill-card {
            padding: 20px;
            border-radius: 16px;
          }

          .bill-search-bar {
            flex-direction: column;
          }

          .bill-search-input,
          .bill-filter-select {
            min-width: 100%;
          }

          .bill-table {
            font-size: 0.9rem;
          }

          .bill-table th,
          .bill-table td {
            padding: 12px 8px;
          }

          .bill-action-btn {
            padding: 6px 12px;
            font-size: 0.85rem;
            margin-right: 4px;
            margin-bottom: 4px;
          }

          .bill-modal-content {
            padding: 20px;
            margin: 10px;
          }

          .bill-modal-title {
            font-size: 1.3rem;
          }
        }

        @media (max-width: 480px) {
          .bill-container {
            padding: 10px;
          }

          .bill-title {
            font-size: 1.5rem;
          }

          .bill-card {
            padding: 15px;
            border-radius: 12px;
          }

          .bill-table {
            font-size: 0.85rem;
          }

          .bill-table th,
          .bill-table td {
            padding: 10px 6px;
          }

          .bill-table th:nth-child(6),
          .bill-table td:nth-child(6) {
            display: none;
          }

          .bill-action-btn {
            padding: 6px 10px;
            font-size: 0.8rem;
            display: block;
            width: 100%;
            margin-bottom: 8px;
            margin-right: 0;
          }

          .bill-modal-content {
            padding: 15px;
            margin: 5px;
          }

          .bill-btn {
            padding: 10px 16px;
            font-size: 0.9rem;
            width: 100%;
            justify-content: center;
          }

          .bill-search-bar {
            gap: 8px;
          }
        }

        @media (max-width: 360px) {
          .bill-container {
            padding: 8px;
          }

          .bill-title {
            font-size: 1.3rem;
          }

          .bill-card {
            padding: 12px;
          }

          .bill-table {
            font-size: 0.8rem;
          }

          .bill-table th,
          .bill-table td {
            padding: 8px 4px;
          }

          .bill-action-btn {
            padding: 4px 8px;
            font-size: 0.75rem;
          }

          .bill-modal-content {
            padding: 12px;
          }

          .bill-btn {
            padding: 8px 12px;
            font-size: 0.85rem;
          }
        }
      `}</style>

      <div className="bill-container">
        <div className="bill-wrapper">
          <div className="bill-header">
            <h1 className="bill-title">Bill Management</h1>
          </div>

          <div className="bill-card">
            <div className="bill-search-bar">
              <input
                type="text"
                className="bill-search-input"
                placeholder="Search bills by party, lot number, or process..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
              <SearchableSelect
                options={[{ _id: "", name: "All Parties" }, ...parties]}
                value={selectedPartyFilter}
                onChange={(e) => handlePartyFilter(e.target.value)}
                placeholder="Filter by party..."
                getOptionLabel={(party) => party?.name || "All Parties"}
                getOptionValue={(party) => party?._id ?? ""}
                searchFields={["name", "number", "_id"]}
                className="bill-filter-select"
                showSearchIcon={false}
              />
              {user && (
                <button
                  className="bill-btn bill-btn-toggle"
                  onClick={handleToggleView}
                >
                  {showMyBills ? "Show All" : "Show My Bills"}
                </button>
              )}
              <button
                className="bill-btn bill-btn-success"
                onClick={() => navigate("/bill-report")}
              >
                📊 View Report
              </button>
              <button
                className="bill-btn bill-btn-primary"
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
              >
                + Add Bill
              </button>
            </div>

            {loading && <div className="bill-loading">Loading...</div>}

            {!loading && filteredBills.length === 0 && (
              <div className="bill-empty">No bills found</div>
            )}

            {!loading && filteredBills.length > 0 && (
              <div className="bill-table-wrapper">
                <table className="bill-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Party</th>
                      <th>Lot Number</th>
                      <th>Process</th>
                      <th>Quantity</th>
                      <th>Amount</th>
                      <th>Total</th>
                      <th>Paid</th>
                      <th>Outstanding</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBills.map((bill) => (
                      <tr key={bill._id}>
                        <td>{new Date(bill.date).toLocaleDateString()}</td>
                        <td>
                          <strong>{bill.party?.name || "N/A"}</strong>
                        </td>
                        <td>{bill.lotNumber}</td>
                        <td>{bill.process?.name || "N/A"}</td>
                        <td>{bill.quantity}</td>
                        <td>₹{parseFloat(bill.amount).toFixed(2)}</td>
                        <td className="bill-total">
                          ₹{parseFloat(bill.total).toFixed(2)}
                        </td>
                        <td>₹{parseFloat(bill.paidAmount ?? 0).toFixed(2)}</td>
                        <td className={bill.outstanding > 0 ? "bill-outstanding-due" : ""}>
                          ₹{parseFloat(bill.outstanding ?? bill.total ?? 0).toFixed(2)}
                        </td>
                        <td>
                          <span className={`bill-status bill-status-${(bill.status || "Unpaid").toLowerCase()}`}>
                            {bill.status || "Unpaid"}
                          </span>
                        </td>
                        <td>
                          <button
                            className="bill-action-btn bill-btn-edit"
                            onClick={() => handleEdit(bill)}
                          >
                            Edit
                          </button>
                          <button
                            className="bill-action-btn bill-btn-delete"
                            onClick={() => handleDelete(bill._id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && paginatedMode && (pagination?.totalPages || 0) > 1 && (
              <div className="bill-pagination" style={{ marginTop: "16px", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="bill-btn"
                  disabled={pagination?.page <= 1}
                  onClick={() => fetchPaginated((pagination?.page || 1) - 1, pagination?.limit || 20)}
                >
                  ← Prev
                </button>
                <span style={{ fontWeight: 600 }}>
                  Page {pagination?.page || 1} of {pagination?.totalPages || 1}
                  {pagination?.total != null && ` (${pagination.total} total)`}
                </span>
                <button
                  type="button"
                  className="bill-btn"
                  disabled={(pagination?.page || 1) >= (pagination?.totalPages || 1)}
                  onClick={() => fetchPaginated((pagination?.page || 1) + 1, pagination?.limit || 20)}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="bill-modal" onClick={() => setShowModal(false)}>
          <div
            className="bill-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bill-modal-header">
              <h2 className="bill-modal-title">
                {isEditing ? "Edit Bill" : "Add New Bill"}
              </h2>
              <button
                className="bill-modal-close"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="bill-form-group">
                <label className="bill-label required" htmlFor="date">
                  Date
                </label>
                <input
                  id="date"
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("date")}
                  className={`bill-input ${
                    errors.date && touched.date ? "error" : ""
                  }`}
                  disabled={loading}
                />
                {errors.date && touched.date && (
                  <div className="bill-error">⚠️ {errors.date}</div>
                )}
              </div>

              <div className="bill-form-group">
                <label className="bill-label required" htmlFor="quantity">
                  Quantity (Pieces)
                </label>
                <input
                  id="quantity"
                  type="text"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("quantity")}
                  placeholder="Enter quantity (e.g., 10, 50)"
                  className={`bill-input ${
                    errors.quantity && touched.quantity ? "error" : ""
                  }`}
                  disabled={loading}
                />
                {errors.quantity && touched.quantity && (
                  <div className="bill-error">⚠️ {errors.quantity}</div>
                )}
              </div>
              <div className="bill-form-group">
                <label className="bill-label required" htmlFor="party">
                  Party
                </label>
                <SearchableSelect
                  name="party"
                  options={parties}
                  value={formData.party}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("party")}
                  placeholder="Search party by name or number..."
                  getOptionLabel={(party) =>
                    party
                      ? `${party.name} - ${party.number}`
                      : ""
                  }
                  getOptionValue={(party) => party?._id ?? ""}
                  searchFields={["name", "number", "_id"]}
                  className={`bill-select ${
                    errors.party && touched.party ? "error" : ""
                  }`}
                  error={!!(errors.party && touched.party)}
                  disabled={loading}
                  required
                />
                {errors.party && touched.party && (
                  <div className="bill-error">⚠️ {errors.party}</div>
                )}
              </div>

              <div className="bill-form-group">
                <label className="bill-label required" htmlFor="lotNumber">
                  Lot Number
                </label>
                <input
                  id="lotNumber"
                  type="text"
                  name="lotNumber"
                  value={formData.lotNumber}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("lotNumber")}
                  placeholder="Enter lot number"
                  className={`bill-input ${
                    errors.lotNumber && touched.lotNumber ? "error" : ""
                  }`}
                  disabled={loading}
                />
                {errors.lotNumber && touched.lotNumber && (
                  <div className="bill-error">⚠️ {errors.lotNumber}</div>
                )}
              </div>

              <div className="bill-form-group">
                <label className="bill-label required" htmlFor="process">
                  Process
                </label>
                <SearchableSelect
                  name="process"
                  options={processes}
                  value={formData.process}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("process")}
                  placeholder="Search process by name..."
                  getOptionLabel={(process) =>
                    process
                      ? `${process.name} - ₹${parseFloat(process.amount).toFixed(
                          2
                        )}`
                      : ""
                  }
                  getOptionValue={(process) => process?._id ?? ""}
                  searchFields={["name", "_id"]}
                  className={`bill-select ${
                    errors.process && touched.process ? "error" : ""
                  }`}
                  error={!!(errors.process && touched.process)}
                  disabled={loading}
                  required
                />
                {errors.process && touched.process && (
                  <div className="bill-error">⚠️ {errors.process}</div>
                )}
              </div>

              <div className="bill-form-group">
                <label className="bill-label required" htmlFor="amount">
                  Amount (per piece)
                </label>
                <input
                  id="amount"
                  type="text"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("amount")}
                  placeholder="Enter amount per piece"
                  className={`bill-input ${
                    errors.amount && touched.amount ? "error" : ""
                  }`}
                  disabled={loading}
                />
                {errors.amount && touched.amount && (
                  <div className="bill-error">⚠️ {errors.amount}</div>
                )}
              </div>

              {formData.total && (
                <div className="bill-total-display">
                  <div className="bill-total-label">Total Amount</div>
                  <div className="bill-total-value">
                    ₹{parseFloat(formData.total).toFixed(2)}
                  </div>
                </div>
              )}

              <div className="bill-modal-actions">
                <button
                  type="button"
                  className="bill-btn bill-btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bill-btn bill-btn-primary"
                  disabled={loading}
                >
                  {loading ? "Saving..." : isEditing ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Bill;
